import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Plus,
  Search,
  X,
  Edit,
  Trash2,
  Eye,
  ChefHat,
  Package,
  FileText,
  Layers,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  get_view_receipt,
  delete_recipe,
  show_hide_recipe,
} from "../ServiceConnection/serviceconnection";

const num = (v, d = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : d;
};

const fmt = (v) => {
  const n = num(v);
  if (!n) return "0";
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 1000) / 1000);
};

export default function ViewRecipe() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [recipes, setRecipes] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const [ingredientsDialogOpen, setIngredientsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);

  /* ---------------- fetch ---------------- */

  const fetchRecipes = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("call_id", "0");
    fd.append("flag", "0");

    try {
      const response = await server_post_data(get_view_receipt, fd);

      if (response.data.error) {
        setRecipes([]);
        toast({
          title: "Could not load recipes",
          description: response.data.message,
          variant: "destructive",
        });
        return;
      }

      const apiData = response.data.message?.final_data || [];

      setRecipes(
        apiData.map((item) => {
          const isSub = num(item.is_sub_recipe) === 1;
          return {
            primary_id: item.primary_id,
            recipe_code: item.recipe_code || "N/A",
            recipe_name: item.recipe_name || "Untitled Recipe",
            recipe_type: item.recipe_type || (isSub ? "Sub Recipe" : "Item"),
            is_sub_recipe: isSub,
            menu_item: item.item_name || item.addon_item_name || "—",
            variant: item.variant_name || "",
            department_name: item.department_name || "",
            department_id: num(item.department_id),
            prepared_qty: num(item.recipe_output_qty, 0),
            prepared_unit: item.recipe_output_unit_name || "",
            total_ingredients: item.ingredients?.length || 0,
            active_status: num(item.active_status) === 1,
            ingredients: item.ingredients || [],
          };
        }),
      );
    } catch (error) {
      console.error("recipes:", error);
      setRecipes([]);
      toast({
        title: "Network error",
        description: "Could not reach the server. Try again.",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchRecipes();
  }, [fetchRecipes]);

  /* ---------------- stats ---------------- */

  const stats = useMemo(
    () => ({
      totalRecipes: recipes.length,
      activeRecipes: recipes.filter((r) => r.active_status).length,
      itemRecipes: recipes.filter((r) => r.recipe_type === "Item").length,
      addonRecipes: recipes.filter((r) => r.recipe_type === "Addon").length,
      subRecipes: recipes.filter((r) => r.is_sub_recipe).length,
    }),
    [recipes],
  );

  const filteredRecipes = useMemo(() => {
    return recipes.filter((row) => {
      if (typeFilter === "sub" && !row.is_sub_recipe) return false;
      if (typeFilter === "item" && row.recipe_type !== "Item") return false;
      if (typeFilter === "addon" && row.recipe_type !== "Addon") return false;

      if (!searchFilter) return true;
      const s = searchFilter.toLowerCase();
      return (
        row.recipe_code?.toLowerCase().includes(s) ||
        row.recipe_name?.toLowerCase().includes(s) ||
        row.menu_item?.toLowerCase().includes(s) ||
        row.department_name?.toLowerCase().includes(s)
      );
    });
  }, [recipes, searchFilter, typeFilter]);

  /* ---------------- actions ---------------- */

  const handleViewIngredients = (recipe) => {
    setSelectedRecipe(recipe);
    setIngredientsDialogOpen(true);
  };

  const handleEdit = (recipeId) => navigate(`/edit-recipe/${recipeId}`);

  const handleDeleteClick = (recipe) => {
    setSelectedRecipe(recipe);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedRecipe) return;
    setActionBusy(true);

    const fd = new FormData();
    fd.append("recipe_id", selectedRecipe.primary_id);

    try {
      const res = await server_post_data(delete_recipe, fd);
      if (res.data.error) {
        toast({
          title: "Could not delete",
          description: res.data.message,
          variant: "destructive",
        });
      } else {
        toast({ title: "Deleted", description: "Recipe removed" });
        await fetchRecipes();
      }
    } catch (e) {
      console.error("delete:", e);
      toast({
        title: "Network error",
        description: "Could not reach the server. Try again.",
        variant: "destructive",
      });
    } finally {
      setActionBusy(false);
      setDeleteDialogOpen(false);
      setSelectedRecipe(null);
    }
  };

  const handleStatusToggle = (recipe) => {
    setSelectedRecipe(recipe);
    setStatusDialogOpen(true);
  };

  const confirmStatusChange = async () => {
    if (!selectedRecipe) return;
    setActionBusy(true);

    const nextStatus = selectedRecipe.active_status ? 0 : 1;
    const fd = new FormData();
    fd.append("recipe_id", selectedRecipe.primary_id);
    fd.append("active_status", String(nextStatus));

    try {
      const res = await server_post_data(show_hide_recipe, fd);
      if (res.data.error) {
        toast({
          title: "Could not update",
          description: res.data.message,
          variant: "destructive",
        });
      } else {
        setRecipes((prev) =>
          prev.map((r) =>
            r.primary_id === selectedRecipe.primary_id
              ? { ...r, active_status: nextStatus === 1 }
              : r,
          ),
        );
        toast({
          title: nextStatus === 1 ? "Activated" : "Deactivated",
          description: selectedRecipe.recipe_name,
        });
      }
    } catch (e) {
      console.error("status:", e);
      toast({
        title: "Network error",
        description: "Could not reach the server. Try again.",
        variant: "destructive",
      });
    } finally {
      setActionBusy(false);
      setStatusDialogOpen(false);
      setSelectedRecipe(null);
    }
  };

  /* ---------------- badge ---------------- */

  const typeBadgeClass = (type, isSub) => {
    if (isSub) return "bg-warning/10 text-warning border-warning/30";
    if (type === "Item") return "bg-primary/10 text-primary border-primary/30";
    return "bg-success/10 text-success border-success/30";
  };

  /* ---------------- render ---------------- */

  return (
    <MainLayout>
      <Header title="Recipe Management" subtitle="View and manage all recipes" />

      <div className="p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <ChefHat className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalRecipes}
                </p>
                <p className="text-sm text-muted-foreground">Total Recipes</p>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.activeRecipes}
                </p>
                <p className="text-sm text-muted-foreground">Active</p>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Package className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.itemRecipes}
                </p>
                <p className="text-sm text-muted-foreground">Item Recipes</p>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <Plus className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.addonRecipes}
                </p>
                <p className="text-sm text-muted-foreground">Addon Recipes</p>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <Layers className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.subRecipes}
                </p>
                <p className="text-sm text-muted-foreground">Sub Recipes</p>
              </div>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search recipes..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className="pl-10 pr-10"
              />
              {searchFilter && (
                <button
                  onClick={() => setSearchFilter("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-44">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="item">Item</SelectItem>
                <SelectItem value="addon">Addon</SelectItem>
                <SelectItem value="sub">Sub Recipe</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={() => navigate("/add-recipe")}
            className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
          >
            <Plus className="w-4 h-4" />
            Add Recipe
          </Button>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground">Sr.No</TableHead>
                  <TableHead className="text-muted-foreground">Code</TableHead>
                  <TableHead className="text-muted-foreground">
                    Recipe Name
                  </TableHead>
                  <TableHead className="text-muted-foreground">Type</TableHead>
                  <TableHead className="text-muted-foreground">
                    Deducts From
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Ingredients
                  </TableHead>
                  <TableHead className="text-muted-foreground">Status</TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {showLoaderAdmin ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center text-muted-foreground py-10"
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredRecipes.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center text-muted-foreground py-10"
                    >
                      {recipes.length === 0
                        ? "No recipes yet. Add one to start tracking stock."
                        : "No recipes match this search."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRecipes.map((recipe, index) => (
                    <TableRow key={recipe.primary_id} className="border-border">
                      <TableCell>{index + 1}</TableCell>

                      <TableCell>
                        <span className="font-mono text-sm font-semibold text-foreground">
                          {recipe.recipe_code}
                        </span>
                      </TableCell>

                      <TableCell className="font-medium">
                        {recipe.recipe_name}
                        {recipe.variant && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            {recipe.variant}
                          </span>
                        )}
                        {recipe.is_sub_recipe && recipe.prepared_qty > 0 && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Makes {fmt(recipe.prepared_qty)}{" "}
                            {recipe.prepared_unit}
                          </p>
                        )}
                      </TableCell>

                      <TableCell>
                        <Badge
                          variant="outline"
                          className={cn(
                            typeBadgeClass(
                              recipe.recipe_type,
                              recipe.is_sub_recipe,
                            ),
                          )}
                        >
                          {recipe.is_sub_recipe
                            ? "Sub Recipe"
                            : recipe.recipe_type}
                        </Badge>
                      </TableCell>

                      <TableCell className="text-sm text-muted-foreground">
                        {recipe.is_sub_recipe || recipe.recipe_type === "Addon"
                          ? "Same as dish"
                          : recipe.department_name || "Main Store"}
                      </TableCell>

                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewIngredients(recipe)}
                          className="gap-2"
                        >
                          <Eye className="w-4 h-4" />
                          View ({recipe.total_ingredients})
                        </Button>
                      </TableCell>

                      <TableCell>
                        <Switch
                          checked={recipe.active_status}
                          onCheckedChange={() => handleStatusToggle(recipe)}
                        />
                      </TableCell>

                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(recipe.primary_id)}
                            className="hover:bg-primary/10 hover:text-primary"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(recipe)}
                            className="hover:bg-destructive/10 hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* ---------- Ingredients Dialog ---------- */}
      <Dialog
        open={ingredientsDialogOpen}
        onOpenChange={setIngredientsDialogOpen}
      >
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedRecipe?.recipe_name || "Recipe"} — Ingredients
            </DialogTitle>
            <DialogDescription asChild>
              <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm">
                <span>
                  <span className="text-muted-foreground">Code:</span>{" "}
                  {selectedRecipe?.recipe_code}
                </span>
                <span>
                  <span className="text-muted-foreground">Type:</span>{" "}
                  {selectedRecipe?.is_sub_recipe
                    ? "Sub Recipe"
                    : selectedRecipe?.recipe_type}
                </span>
                {!selectedRecipe?.is_sub_recipe && (
                  <span>
                    <span className="text-muted-foreground">Menu item:</span>{" "}
                    {selectedRecipe?.menu_item || "—"}
                  </span>
                )}
                {selectedRecipe?.is_sub_recipe &&
                  selectedRecipe?.prepared_qty > 0 && (
                    <span>
                      <span className="text-muted-foreground">Makes:</span>{" "}
                      {fmt(selectedRecipe.prepared_qty)}{" "}
                      {selectedRecipe.prepared_unit}
                    </span>
                  )}
              </div>
            </DialogDescription>
          </DialogHeader>

          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Ingredient</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead className="text-right">In Base Unit</TableHead>
                  <TableHead className="text-right">Yield</TableHead>
                  <TableHead className="text-right">Waste</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedRecipe?.ingredients?.length > 0 ? (
                  selectedRecipe.ingredients.map((ing, index) => {
                    const isRU = num(ing.is_recipe_unit) === 1;
                    const isSub = num(ing.source_recipe_id) > 0;
                    const conv = num(ing.conversion_used);
                    const qtyBase = num(ing.quantity);

                    // Recipe unit: frozen rate se wapas nikalo (5 Leaf)
                    let displayQty = num(ing.item_qty_entry);
                    if (isRU && conv > 0) {
                      displayQty = Math.round((qtyBase / conv) * 1000) / 1000;
                    }
                    if (!displayQty) displayQty = qtyBase;

                    const displayUnit = isRU
                      ? ing.recipe_unit_name || ""
                      : ing.item_unit_entry_name || ing.unit_name || "";

                    const name = String(ing.ingredient_name || "");

                    return (
                      <TableRow key={ing.primary_id || index}>
                        <TableCell>{index + 1}</TableCell>

                        <TableCell className="font-medium">
                          {name}
                          {isSub && (
                            <Badge
                              variant="outline"
                              className="ml-2 bg-warning/10 text-warning border-warning/30"
                            >
                              Sub Recipe
                            </Badge>
                          )}
                          {isRU && (
                            <Badge
                              variant="outline"
                              className="ml-2 bg-primary/10 text-primary border-primary/30"
                            >
                              Recipe unit
                            </Badge>
                          )}
                        </TableCell>

                        <TableCell className="text-right font-medium">
                          {fmt(displayQty)} {displayUnit}
                        </TableCell>

                        <TableCell className="text-right text-muted-foreground">
                          {fmt(qtyBase)} {ing.unit_name || ""}
                        </TableCell>

                        <TableCell className="text-right text-muted-foreground">
                          {num(ing.yield_quantity)
                            ? `${fmt(ing.yield_quantity)} ${ing.unit_name || ""}`
                            : "—"}
                        </TableCell>

                        <TableCell className="text-right text-muted-foreground">
                          {num(ing.waste_quantity)
                            ? `${fmt(ing.waste_quantity)} ${ing.unit_name || ""}`
                            : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center text-muted-foreground py-8"
                    >
                      No ingredients in this recipe
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <p className="text-xs text-muted-foreground">
            Stock is always deducted in the base unit. The quantity column shows
            what was entered when the recipe was saved.
          </p>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIngredientsDialogOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Delete ---------- */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this recipe?</AlertDialogTitle>
            <AlertDialogDescription>
              "{selectedRecipe?.recipe_name}" will be removed. Orders for this
              item will stop deducting stock.
              {selectedRecipe?.is_sub_recipe &&
                " If other recipes use it, the delete will be blocked."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={actionBusy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {actionBusy ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ---------- Status ---------- */}
      <AlertDialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {selectedRecipe?.active_status ? "Deactivate" : "Activate"} recipe?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedRecipe?.active_status
                ? `"${selectedRecipe?.recipe_name}" will stop deducting stock when ordered.`
                : `"${selectedRecipe?.recipe_name}" will start deducting stock when ordered.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmStatusChange} disabled={actionBusy}>
              {actionBusy ? "Saving..." : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainLayout>
  );
}